import React, { useState, useMemo } from 'react';

function MortalityAnalytics({ mortalities }) {
  const [timeframe, setTimeframe] = useState('daily'); // daily, monthly, yearly

  const chartData = useMemo(() => {
    const grouped = mortalities.reduce((acc, m) => {
      const dateObj = new Date(m.date);
      let key;
      if (timeframe === 'yearly') key = dateObj.getFullYear().toString();
      else if (timeframe === 'monthly') key = `${dateObj.getFullYear()}-${(dateObj.getMonth() + 1).toString().padStart(2, '0')}`;
      else key = dateObj.toLocaleDateString();

      if (!acc[key]) acc[key] = { label: key, deaths: 0, feeds: 0, sortKey: dateObj.getTime() };
      acc[key].deaths += m.number_of_deaths;
      acc[key].feeds += m.number_of_feeds;
      if (dateObj.getTime() < acc[key].sortKey) acc[key].sortKey = dateObj.getTime();
      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => a.sortKey - b.sortKey);
  }, [mortalities, timeframe]);

  const locationData = useMemo(() => {
    const grouped = mortalities.reduce((acc, m) => {
      const key = m.location_name || 'Unknown';
      if (!acc[key]) acc[key] = { label: key, deaths: 0, feeds: 0 };
      acc[key].deaths += m.number_of_deaths;
      acc[key].feeds += m.number_of_feeds;
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => b.deaths - a.deaths);
  }, [mortalities]);

  const employeeData = useMemo(() => {
    const grouped = mortalities.reduce((acc, m) => {
      const key = m.employee_name || 'Unknown';
      if (!acc[key]) acc[key] = { label: key, deaths: 0, feeds: 0 };
      acc[key].deaths += m.number_of_deaths;
      acc[key].feeds += m.number_of_feeds;
      return acc;
    }, {});
    return Object.values(grouped).sort((a, b) => b.deaths - a.deaths);
  }, [mortalities]);

  const stats = useMemo(() => {
    return mortalities.reduce((acc, m) => {
      acc.deaths += m.number_of_deaths;
      acc.feeds += m.number_of_feeds;
      return acc;
    }, { deaths: 0, feeds: 0 });
  }, [mortalities]);

  const maxDeaths = Math.max(...chartData.map(d => d.deaths), 1);
  const maxFeeds = Math.max(...chartData.map(d => d.feeds), 1);
  const maxLocDeaths = Math.max(...locationData.map(d => d.deaths), 1);
  const maxLocFeeds = Math.max(...locationData.map(d => d.feeds), 1);
  const maxEmpDeaths = Math.max(...employeeData.map(d => d.deaths), 1);
  const maxEmpFeeds = Math.max(...employeeData.map(d => d.feeds), 1);

  return (
    <div className="form-section" style={{marginBottom: '2rem'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'}}>
        <h3 style={{margin: 0}}>Mortality & Feed Analytics</h3>
        <div className="button-group" style={{display: 'flex', gap: '0.5rem'}}>
          {['daily', 'monthly', 'yearly'].map(t => (
            <button 
              key={t}
              onClick={() => setTimeframe(t)}
              className="action-btn"
              style={{
                backgroundColor: timeframe === t ? '#34495e' : '#bdc3c7',
                fontSize: '0.8rem',
                padding: '0.25rem 0.75rem'
              }}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem'}}>
        <div style={{padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center', border: '1px solid #eee'}}>
          <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem'}}>Total Deaths</div>
          <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#e74c3c'}}>{stats.deaths.toLocaleString()}</div>
        </div>
        <div style={{padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center', border: '1px solid #eee'}}>
          <div style={{fontSize: '0.8rem', color: '#666', marginBottom: '0.5rem'}}>Total Feeds</div>
          <div style={{fontSize: '1.5rem', fontWeight: 'bold', color: '#2c3e50'}}>{stats.feeds.toLocaleString()}</div>
        </div>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem'}}>
        <div>
          <h4>Mortality Trend</h4>
          <div style={{height: '200px', display: 'flex', alignItems: 'flex-end', gap: '4px', borderBottom: '2px solid #ddd', paddingBottom: '5px'}}>
            {chartData.map((d, i) => (
              <div 
                key={i} 
                style={{
                  flex: 1, 
                  height: `${(d.deaths / maxDeaths) * 100}%`, 
                  backgroundColor: '#e74c3c',
                  minWidth: timeframe === 'daily' ? '5px' : '30px',
                  transition: 'height 0.3s ease'
                }} 
                title={`${d.label}: ${d.deaths} deaths`}
              />
            ))}
          </div>
        </div>
        <div>
          <h4>Feed Trend</h4>
          <div style={{height: '200px', display: 'flex', alignItems: 'flex-end', gap: '4px', borderBottom: '2px solid #ddd', paddingBottom: '5px'}}>
            {chartData.map((d, i) => (
              <div 
                key={i} 
                style={{
                  flex: 1, 
                  height: `${(d.feeds / maxFeeds) * 100}%`, 
                  backgroundColor: '#3498db',
                  minWidth: timeframe === 'daily' ? '5px' : '30px',
                  transition: 'height 0.3s ease'
                }} 
                title={`${d.label}: ${d.feeds} feeds`}
              />
            ))}
          </div>
        </div>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem'}}>
        <div>
          <h4>By Location (Deaths)</h4>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
            {locationData.map((d, i) => (
              <div key={i}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px'}}>
                  <span>{d.label}</span>
                  <strong>{d.deaths}</strong>
                </div>
                <div style={{height: '12px', backgroundColor: '#eee', borderRadius: '6px', overflow: 'hidden'}}>
                  <div style={{height: '100%', width: `${(d.deaths / maxLocDeaths) * 100}%`, backgroundColor: '#e74c3c'}} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4>By Location (Feeds)</h4>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
            {locationData.map((d, i) => (
              <div key={i}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px'}}>
                  <span>{d.label}</span>
                  <strong>{d.feeds}</strong>
                </div>
                <div style={{height: '12px', backgroundColor: '#eee', borderRadius: '6px', overflow: 'hidden'}}>
                  <div style={{height: '100%', width: `${(d.feeds / maxLocFeeds) * 100}%`, backgroundColor: '#3498db'}} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem'}}>
        <div>
          <h4>By Employee (Deaths)</h4>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
            {employeeData.map((d, i) => (
              <div key={i}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px'}}>
                  <span>{d.label}</span>
                  <strong>{d.deaths}</strong>
                </div>
                <div style={{height: '12px', backgroundColor: '#eee', borderRadius: '6px', overflow: 'hidden'}}>
                  <div style={{height: '100%', width: `${(d.deaths / maxEmpDeaths) * 100}%`, backgroundColor: '#e67e22'}} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div>
          <h4>By Employee (Feeds)</h4>
          <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
            {employeeData.map((d, i) => (
              <div key={i}>
                <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '4px'}}>
                  <span>{d.label}</span>
                  <strong>{d.feeds}</strong>
                </div>
                <div style={{height: '12px', backgroundColor: '#eee', borderRadius: '6px', overflow: 'hidden'}}>
                  <div style={{height: '100%', width: `${(d.feeds / maxEmpFeeds) * 100}%`, backgroundColor: '#27ae60'}} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default MortalityAnalytics;
